import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import DataTable, { Column } from '../../common/DataTable';
import StatusBadge, { StatusType } from '../../common/StatusBadge';
import apiService from '../../../services/api';
import { usePermissionAccess } from '../../auth/RoleGuard';
import { RecipientState, SurveyRecipient } from '../types';

/**
 * Per-recipient funnel. The point of this tab is not the list — it is the filter:
 * "Opened, never finished" and "Not yet opened" are the two follow-up lists worth
 * acting on, so they are one click away rather than something to derive by eye.
 */

const STATE_LABEL: Record<RecipientState, string> = {
  invited: 'Invited',
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  clicked: 'Clicked',
  viewed: 'Landed',
  completed: 'Completed',
  bounced: 'Bounced',
};

/** Maps our funnel states onto the shared badge's fixed vocabulary. */
const STATE_BADGE: Record<RecipientState, StatusType> = {
  invited: 'pending',
  sent: 'pending',
  delivered: 'inactive',
  opened: 'warning',
  clicked: 'warning',
  viewed: 'verified',
  completed: 'success',
  bounced: 'error',
};

const FILTERS: Array<{ id: string; label: string; state?: RecipientState }> = [
  { id: 'all', label: 'Everyone' },
  { id: 'completed', label: 'Completed', state: 'completed' },
  { id: 'viewed', label: 'Landed, not finished', state: 'viewed' },
  { id: 'opened', label: 'Opened, not landed', state: 'opened' },
  { id: 'delivered', label: 'Delivered, not opened', state: 'delivered' },
  { id: 'sent', label: 'Not yet delivered', state: 'sent' },
  { id: 'bounced', label: 'Bounced', state: 'bounced' },
];

/**
 * Deliberately terse: seven timestamp columns with "Jul 28, 11:40 AM" in each wraps
 * to three lines per cell and pushes the last column off the page. Day + 24h time
 * is unambiguous within a campaign window, and the full value is in the CSV.
 */
const fmt = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const day = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${time}`;
};

/** Keeps a timestamp on one line and carries the exact value in a tooltip. */
const Stamp: React.FC<{ iso: string | null; suffix?: React.ReactNode }> = ({ iso, suffix }) => (
  <Tooltip title={iso ? new Date(iso).toLocaleString() : ''} arrow disableHoverListener={!iso}>
    <Typography
      component="span"
      sx={{
        fontFamily: "'Montserrat', sans-serif",
        fontSize: '12.5px',
        color: iso ? '#374151' : '#D1D5DB',
        whiteSpace: 'nowrap',
      }}
    >
      {fmt(iso)}
      {suffix}
    </Typography>
  </Tooltip>
);

interface Props {
  /** Bumped by the parent after a sync so the table refetches. */
  refreshKey?: number;
  /** Which survey to show. Null while the switcher is still loading. */
  campaign?: string | null;
}

export const RecipientsTab: React.FC<Props> = ({ refreshKey = 0, campaign = null }) => {
  const { hasFullAccess } = usePermissionAccess();
  const canEdit = hasFullAccess('audience_manager');

  const [rows, setRows] = useState<SurveyRecipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [confirm, setConfirm] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetched unfiltered and filtered in the browser: the list is tens of rows,
      // and it makes the filter chips instant with no refetch per click.
      const res = await apiService.getSurveyRecipients({ campaign: campaign || undefined });
      setRows(res?.recipients || []);
    } catch (e: any) {
      // Deliberately surfaced rather than swallowed — an empty table that is
      // actually a 503 is indistinguishable from "nobody invited yet".
      setError(e?.message === 'Unauthorized' ? 'Your session expired. Reload to sign in again.' : e?.message || 'Could not load recipients');
      setRows([]);
    }
    setLoading(false);
    // `campaign` MUST be here. Without it the memoised loader captures whatever the
    // prop was on first render — null, before the switcher has resolved — so every
    // later fetch went out unscoped and the Lambda served the default campaign. The
    // effect below re-fired on a switch and called this same stale function.
  }, [campaign]);

  useEffect(() => {
    load();
  }, [load, refreshKey, campaign]);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: rows.length };
    for (const r of rows) c[r.state] = (c[r.state] || 0) + 1;
    return c;
  }, [rows]);

  const filtered = useMemo(() => {
    const active = FILTERS.find((f) => f.id === filter);
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (active?.state && r.state !== active.state) return false;
      if (term && !r.email.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rows, filter, search]);

  useEffect(() => {
    setPage(0);
  }, [filter, search]);

  const removeFromList = async (email: string) => {
    setRemoving(email);
    setConfirm(null);
    setNote(null);
    try {
      await apiService.suppressSurveyRecipient(email, {
        reason: 'Removed from the survey list by an admin',
      });
      setNote(`${email} removed. They will not be re-added or emailed again.`);
      await load();
    } catch (e: any) {
      setNote(
        e?.message?.includes('PERMISSION') || e?.message?.includes('403')
          ? 'You have view-only access to this area, so nobody can be removed.'
          : `Could not remove ${email}: ${e?.message || 'unknown error'}`
      );
    }
    setRemoving(null);
  };

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const columns: Column<SurveyRecipient>[] = [
    {
      id: 'email',
      label: 'Member',
      render: (r) => (
        <Box>
          <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 600, color: '#111827' }}>
            {r.email}
          </Typography>
          {r.no_invite && (
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
              no invite — typed their address
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'state',
      label: 'Furthest step',
      render: (r) => <StatusBadge status={STATE_BADGE[r.state]} label={STATE_LABEL[r.state]} size="sm" />,
    },
    {
      id: 'invite_sent_at',
      label: 'Sent',
      render: (r) => <Stamp iso={r.invite_sent_at || r.invite_requested_at} />,
    },
    { id: 'invite_delivered_at', label: 'Delivered', render: (r) => <Stamp iso={r.invite_delivered_at} /> },
    {
      id: 'invite_opened_at',
      label: 'Opened',
      render: (r) => (
        <Stamp
          iso={r.invite_opened_at}
          suffix={
            r.invite_open_count > 1 ? (
              <Typography component="span" sx={{ fontSize: '11px', color: '#6B7280', ml: 0.5 }}>
                ×{r.invite_open_count}
              </Typography>
            ) : null
          }
        />
      ),
    },
    { id: 'first_viewed_at', label: 'Landed', render: (r) => <Stamp iso={r.first_viewed_at} /> },
    { id: 'completed_at', label: 'Completed', render: (r) => <Stamp iso={r.completed_at} /> },
    {
      id: 'actions',
      label: '',
      // Removal lives here, on the row, because this is where you notice that
      // someone should not be on the list. It suppresses rather than deletes: the
      // Refresh button reads live from Aurora, so anything merely deleted would
      // reappear on the next press and be mailed again.
      render: (r) => (
        <Tooltip
          title={
            canEdit
              ? 'Remove from the survey list and never contact this address again'
              : 'You have view-only access to this area.'
          }
          arrow
        >
          <span>
            <IconButton
              size="small"
              disabled={!canEdit || removing === r.email}
              onClick={() => setConfirm(r.email)}
              aria-label={`Remove ${r.email} from the survey list`}
              sx={{ color: '#9CA3AF', '&:hover': { color: '#7D1935', bgcolor: '#FEF2F2' } }}
            >
              {removing === r.email ? (
                <CircularProgress size={14} />
              ) : (
                <BlockIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        {FILTERS.map((f) => {
          const n = f.state ? counts[f.state] || 0 : counts.all || 0;
          return (
            <Chip
              key={f.id}
              label={`${f.label} (${n})`}
              onClick={() => setFilter(f.id)}
              variant={filter === f.id ? 'filled' : 'outlined'}
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '12px',
                fontWeight: filter === f.id ? 700 : 500,
                bgcolor: filter === f.id ? '#1A5F7A' : 'transparent',
                color: filter === f.id ? '#fff' : '#374151',
                borderColor: '#E5E7EB',
                '&:hover': { bgcolor: filter === f.id ? '#0F3D50' : '#F9FAFB' },
              }}
            />
          );
        })}
        <Box sx={{ flexGrow: 1 }} />
        <TextField
          size="small"
          placeholder="Search email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 220, '& .MuiInputBase-input': { fontFamily: "'Montserrat', sans-serif", fontSize: '13px' } }}
        />
      </Box>

      {error && (
        <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#7D1935', mb: 1.5 }}>
          {error}
        </Typography>
      )}

      {note && (
        <Alert
          severity={/^Could not|view-only/.test(note) ? 'warning' : 'success'}
          onClose={() => setNote(null)}
          sx={{ mb: 1.5, fontSize: 13 }}
        >
          {note}
        </Alert>
      )}

      <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700 }}>
          Remove from the survey list?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#374151' }}>
            <strong>{confirm}</strong> will be taken off the list and added to the
            exclusion list, so a future Refresh cannot add them back and no further
            survey email will reach them.
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 1.5 }}>
            Any answers they have already given are kept.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none', color: '#6B7280' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => confirm && removeFromList(confirm)}
            sx={{ textTransform: 'none' }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <DataTable<SurveyRecipient>
        columns={columns}
        data={paged}
        loading={loading}
        emptyMessage={
          rows.length === 0
            ? 'No invitations issued yet. Run send-campaign.mjs to invite the send list.'
            : 'No recipients match this filter.'
        }
        pagination={{
          page,
          rowsPerPage,
          total: filtered.length,
          onPageChange: setPage,
          onRowsPerPageChange: (n) => {
            setRowsPerPage(n);
            setPage(0);
          },
        }}
      />
    </Box>
  );
};

export default RecipientsTab;
